export type CompletenessRule = {
  key: string; label: string; kind: 'textAny' | 'choice' | 'preference' | 'source' | 'multiChoice';
  fields: string[]; options?: string[]; appliesTo?: string;
};
export type CustomerCompleteness = {
  version: string; score: number; completed: number; total: number;
  items: { key: string; label: string; complete: boolean }[];
  missing: { key: string; label: string }[];
};
export type CompletenessRules = { version: string; rules: CompletenessRule[] };

const filled = (value: unknown) => typeof value === 'string' && Boolean(value.trim()) && !/^[*＊]+$/.test(value.trim());

// The server supplies the rubric. For protected fields, use its original
// presence flags rather than draw conclusions from a displayed contact mask.
export function previewCustomerCompleteness(
  customer: Record<string, unknown>, rubric: CompletenessRules,
  original?: CustomerCompleteness, protectedKeys: ReadonlySet<string> = new Set(),
): CustomerCompleteness | null {
  const originalItems = new Map(original?.items.map(item => [item.key, item.complete]));
  const items: CustomerCompleteness['items'] = [];
  for (const rule of rubric.rules) {
    if (rule.appliesTo && customer.productCategory !== rule.appliesTo) continue;
    let complete = false;
    if (protectedKeys.has(rule.key)) {
      if (!originalItems.has(rule.key) || original?.version !== rubric.version) return null;
      complete = originalItems.get(rule.key)!;
    } else if (rule.kind === 'textAny') {
      complete = rule.fields.some(field => filled(customer[field]));
    } else if (rule.kind === 'choice') {
      complete = rule.fields.some(field => typeof customer[field] === 'string' && rule.options?.includes(customer[field] as string));
    } else if (rule.kind === 'source') {
      const source = typeof customer.source === 'string' ? customer.source.trim() : '';
      complete = filled(source) && (!['其他渠道', '其他'].includes(source) || filled(customer.sourceNote));
    } else if (rule.kind === 'multiChoice') {
      complete = customer.businessChannels != null
        ? Array.isArray(customer.businessChannels) && customer.businessChannels.some(filled)
        : filled(customer.businessChannel);
    } else {
      const dimension = rule.key.startsWith('preference.') ? rule.key.slice('preference.'.length) : '';
      const preferences = customer.preferences;
      const values = preferences && typeof preferences === 'object' && !Array.isArray(preferences)
        ? (preferences as Record<string, unknown>)[dimension] : undefined;
      complete = Array.isArray(values) && values.length > 0 && values.every(value => typeof value === 'string' && rule.options?.includes(value));
    }
    items.push({ key: rule.key, label: rule.label, complete });
  }
  const completed = items.filter(item => item.complete).length;
  return {
    version: rubric.version, score: items.length ? Math.round(completed / items.length * 100) : 0,
    completed, total: items.length, items,
    missing: items.filter(item => !item.complete).map(({ key, label }) => ({ key, label })),
  };
}
